import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Collapsible,
  HStack,
  Icon,
  LinkBox,
  Stack,
  StackProps,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react'
import { ChevronRightIcon, ChevronUpIcon } from '@src/components/sco/icons'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { useDebounce, useHover } from '@uidotdev/usehooks'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import React, { ReactNode, useMemo, useState } from 'react'
import { LuGrid3X3 } from 'react-icons/lu'

export type SidebarCollapsingNavListItem = {
  activeExact?: boolean
  activePaths?: string[]
  href: string
  icon?: any
  items?: SidebarCollapsingNavListItem[]
  nonActivePaths?: string[]
  title: string
}

export type SidebarCollapsingNavListProps = {
  collapsed?: boolean
  defaultIcon?: any
  defaultTitle?: string
  items?: Array<SidebarCollapsingNavListItem>
  onClick?: () => void
  boxProps?: BoxProps
  buttonProps?: ButtonProps
  stackProps?: StackProps
  hstackProps?: StackProps
  textProps?: TextProps
  navlinkTextProps?: TextProps
}

export const SidebarCollapsingNavList = (props: SidebarCollapsingNavListProps) => {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState<boolean>(!props.collapsed)
  const [hoverRef, isHovering] = useHover()
  const doubouncedIsHovering = useDebounce(isHovering, 250)

  const isOpenOrHovering = isOpen || doubouncedIsHovering

  const activeItem = useMemo(() => {
    return props.items.find(item => {
      let isActive = false
      if (item.activePaths) {
        isActive = [item.href, ...(item.activePaths || [])]
          .filter(Boolean)
          .find(href => (item.activeExact ? href == pathname : pathname?.startsWith(href)))
          ? true
          : false
      } else {
        if (item.activeExact) {
          isActive = pathname == item.href
        } else {
          isActive = pathname?.startsWith(item.href)
        }
      }

      if (item.nonActivePaths?.find(p => p === pathname)) {
        isActive = false
      }

      return isActive
    })
  }, [props.items, pathname])

  return (
    <Box
      ref={hoverRef}
      bgColor={'colorPalette.subtle'}
      border={'1px solid {colors.colorPalette.border}'}
      borderRadius={'lg'}
      width={'full'}
      _hover={{
        borderColor: 'colorPalette.border',
      }}
      {...props.boxProps}
    >
      <Button
        as={'span'}
        boxShadow={'none'}
        padding={4}
        variant={'plain'}
        width={'full'}
        cursor={'pointer'}
        // minHeight={'3.5rem'}
        onClick={() => setIsOpen(!isOpen)}
        {...props.buttonProps}
      >
        <HStack as={'span'} color={'colorPalette.fg'} flex={'1'} textAlign={'left'}>
          <Icon as={activeItem ? activeItem.icon : props.defaultIcon || LuGrid3X3} boxSize={3} />
          <Text as={'span'} textStyle={'labelXs'} {...props.textProps}>
            {activeItem ? activeItem.title : props.defaultTitle}
          </Text>
        </HStack>
        <Icon
          as={ChevronUpIcon}
          color={'colorPalette.fg'}
          transform={!isOpenOrHovering && 'rotate(180deg)'}
          transition={'all 100ms ease-out'}
        />
      </Button>
      <Collapsible.Root open={isOpenOrHovering}>
        <Collapsible.Content>
          <Stack
            bgColor={'colorPalette.panel'}
            borderBottomRadius={'md'}
            borderTop={'1px solid {colors.colorPalette.border}'}
            borderTopRadius={'none'}
            direction={'column'}
            gap={1}
            height={'auto'}
            justify={'stretch'}
            justifyContent={'center'}
            padding={2}
            {...props.stackProps}
          >
            {props.items.map((item, index) => {
              return (
                <NavLink
                  key={index}
                  href={item.href}
                  hstackProps={props.hstackProps}
                  icon={item.icon}
                  isActive={item.href == activeItem?.href}
                  items={item.items}
                  navlinkTextProps={props.navlinkTextProps}
                >
                  {item.title}
                </NavLink>
              )
            })}
          </Stack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}

type NavLinkProps = {
  children: ReactNode | ReactNode[]
  href: string
  icon?: any
  isActive?: boolean
  items: SidebarCollapsingNavListItem[]
  onClick?: () => any
  hstackProps?: StackProps
  navlinkTextProps?: TextProps
}

const NavLink = (props: NavLinkProps) => {
  const isActive = props.isActive

  const onClick = () => {
    if (props.onClick) {
      props.onClick()
    }
  }

  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <>
      <HStack
        asChild
        borderRadius={'md'}
        display={'flex'}
        height={'40px'}
        justify={'space-between'}
        paddingX={3}
        paddingY={2.5}
        transition={'all 0.2s ease-in'}
        width={'full'}
        _hover={{
          color: 'colorPalette.fg',
          bgColor: isActive ? 'colorPalette.emphasized' : 'colorPalette.subtle',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        bg={isActive ? 'colorPalette.emphasized/70' : 'transparent'}
        // border={isActive ? '1px solid {colors.colorPalette.border}' : '1px solid transparent'}
        color={isActive ? 'colorPalette.fg' : 'colorPalette.fg/80'}
        {...props.hstackProps}
      >
        <NextLink href={props.href} onClick={onClick}>
          {props.icon && <Icon as={props.icon} boxSize={3} />}
          <Text
            as={'span'}
            color={isActive ? 'colorPalette.fg' : 'colorPalette.fg/80'}
            fontSize={'sm'}
            fontWeight={isActive ? 'semibold' : 'medium'}
            width={'full'}
            {...props.navlinkTextProps}
          >
            {props.children}
          </Text>
          {isActive ? (
            // Render an invisible placeholder when the NavLink is active
            <span style={{ width: '24px', height: '24px', opacity: 0 }}>
              <Icon as={ChevronRightIcon} boxSize={3} visibility={'hidden'} />
            </span>
          ) : (
            // Render the animated ChevronRightIcon when the NavLink is not active
            <motion.span
              animate={isHovered ? { opacity: 1, x: '0px' } : { opacity: 0, x: '-20px' }}
              initial={{ opacity: 0, x: '-20px' }}
              transition={{ duration: 0.2 }}
            >
              <Icon as={ChevronRightIcon} boxSize={3} />
            </motion.span>
          )}
        </NextLink>
      </HStack>
      {props.items?.length > 0 && (
        <VStack align={'stretch'} gap={0} marginBottom={4} marginTop={2} width={'full'}>
          {props.items?.map((item, idx) => (
            <LinkBox
              key={idx}
              asChild
              borderLeft={'2px solid'}
              borderLeftColor={'colorPalette.border/50'}
              borderRightRadius={'md'}
              color={'colorPalette.fg'}
              fontSize={'xs'}
              fontWeight={'semibold'}
              marginLeft={2}
              paddingLeft={6}
              paddingRight={4}
              paddingY={2}
              _hover={{
                color: 'colorPalette.fg',
                bgColor: 'colorPalette.subtle',
              }}
            >
              <NextLink href={item.href}>{item.title}</NextLink>
            </LinkBox>
          ))}
        </VStack>
      )}
    </>
  )
}
